import { useEffect, useMemo, useState } from "react";
import { Link, useLoaderData, useNavigate, useParams } from "react-router-dom";
import {
  ParkerInfo,
  ParkerSubscription,
  SubListEntry,
  Vehicle,
} from "./Data/ApiTypes";
import { CloseButton, Col, Container, Row, Card, Form, Badge, OverlayTrigger, Tooltip } from "react-bootstrap";
import { Hyperlink } from "./Controls/Hyperlink";
import { Api } from "./Data/Api";
import { processResult } from "./Data/Result";
import { toast } from "react-toastify";
import { ConfirmModal } from "./Controls/ConfirmModal";
import { CancelParkerModal } from "./Controls/CancelParkerModal";
import {
  InviteParkerForm,
  InviteParkerModal,
} from "./UserPlanDetailsPageInviteParkerModal";
import ParkerEditModal from "./ParkerEditModal";
import { parseParkerInfo } from "./Data/ApiParse";
import { SubscriptionStatus } from "./Data/ApiTransport";
import { ColDef } from "ag-grid-community";
import { gridOneCol } from "./Data/Common";
import { PageBar } from "./Controls/PageBar";
import { DataGrid } from "./Controls/DataGrid";
import { Button } from "./Controls/Button";
import { AddVehicleModal } from "./Controls/AddVehicleModal";
import { ChooseVehicleModal } from "./Controls/ChooseVehicleModal";
import { ChevronRight, PersonFill, PSquareFill, Trash, TruckFront } from "react-bootstrap-icons";
import { NoMoreSpacesModal } from "./NoMoreSpacesDialog";
import { useSubscriptionParkersActions } from "./UseSubscriptionParkersActions";
import { getInitials, avatarColor } from "./Data/Avatar";

const MAX_VISIBLE_PLATES = 1;

export function ParkersPage() {
  const [sub, plans] = useLoaderData() as [
    ParkerSubscription[],
    SubListEntry[]
  ];
  const { facilityId, parkerId } = useParams();
  const [allGuestParkers, setAllGuestParkers] = useState<any[]>([]);
  const [showRemove, setShowRemove] = useState(false);
  const [removeParker, setRemoveParker] = useState<ParkerInfo>();
  const [showInvite, setShowInviteParker] = useState(false);
  const [showParkerEditModal, setShowParkerEditModal] = useState(false);
  const [selectedParker, setSelectedParker] = useState<any>();
  const [showAdd, setShowAdd] = useState(false);
  const [showChoose, setShowChoose] = useState(false);
  const [chooseModalVehicles, setChooseModalVehicles] = useState<Vehicle[]>([]);
  const [chooseModalExclude, setChooseModalExclude] = useState<number[]>([]);
  const [search, setSearch] = useState("");
  const [visibleCount, setVisibleCount] = useState(10);
  const [showNoSpacesModal, setShowNoSpacesModal] = useState(false);
  const [newParkerTargetPlanId, setNewParkerTargetPlanId] = useState<number | null>(null);
  const nav = useNavigate();

  useEffect(() => {
    if (!Array.isArray(sub)) return;

    const guests = sub.flatMap((subscription) => {
      if (!Array.isArray(subscription.parkers)) return [];
      return subscription.parkers.map((guest) => ({
        subscriptionId: subscription.subscriptionId,
        sub: subscription,
        parkerId: guest.parker.parkerId,
        name: guest.parker.name,
        email: guest.parker.emailAddress,
        lpn:
          guest.vehicles.length > 0
            ? guest.vehicles.map((v) => v.licensePlateNumber).join(" | ")
            : "N/A",
        plan: subscription.plan.name,
        planId: subscription.plan.planId,
        isManaged: guest.parker.isManaged,
        quantity: guest.quantity,
        parker: guest.parker,
        vehicles: guest.vehicles,
        facilityId: subscription.plan.facilityId,
        subscriptionGroupId: guest.subscriptionGroupId,
      }));
    });

    setAllGuestParkers(guests);
  }, [sub]);

  const {
    vehicleAdd,
    vehicleLink,
    vehicleRemove,
    parkerInvite,
    parkerRemove,
    openChooseModalForParkersList,
  } = useSubscriptionParkersActions({
    subscriptionId: selectedParker?.subscriptionId,
    setParkers: setAllGuestParkers,
    selectedParker,
    setSelectedParker,

    setShowChooseVehicle: setShowChoose,
    setChooseModalVehicles,
    setChooseModalExclude,

    onNoSpaces: () => {
      setShowInviteParker(false);
      setShowNoSpacesModal(true);
    },
  });

  const activePlans = plans.filter(
    (x) => x.status == SubscriptionStatus.Active
  );

  const inviteablePlans = activePlans.filter((x) => !x.isUsageBasedBilling);

  const filterBySearch = (list: any[]) => {
    if (list.length === 0) return list;
    
    return list.filter((p) => 
      p.name?.toLowerCase().includes(search.toLowerCase()) ||
      p.email?.toLowerCase().includes(search.toLowerCase()) ||
      p.lpn?.toLowerCase().includes(search.toLowerCase()) ||
      p.plan?.toLowerCase().includes(search.toLowerCase())
    );
  };

  const filteredParkers = search && allGuestParkers.length > 0 
    ? filterBySearch(allGuestParkers) 
    : allGuestParkers;
  const shownParkers = filteredParkers.slice(0, visibleCount);


  const columns = useMemo<ColDef[]>(
    () => [
      {
        field: "name",
        headerName: "Name",
        sortable: true,
        filter: true,
        flex: 1,
        minWidth: 200,
        cellRenderer: (params: any) => {
          const name = params.value;

          return (
            <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <Hyperlink
                onClick={() => {
                  setSelectedParker(params.data);
                  setShowParkerEditModal(true);
                }}
              >
                {name}
              </Hyperlink>
            </span>
          );
        },
      },
      {
        field: "managedBy",
        headerName: "Managed By",
        sortable: true,
        filter: true,
        flex: 1,
        minWidth: 80,
        valueGetter: (params: any) => {
          return params.data.isManaged ? "Self" : "Group";
        },
        cellRenderer: (params: any) => {
          const isManaged = params.data.isManaged;
          return (
            <span
              className={`badge ${isManaged ? "bg-primary" : "bg-secondary"}`}
              style={{ whiteSpace: "nowrap" }}
            >
              {isManaged ? "Self" : "Group"}
            </span>
          );
        },
      },
      
      {
        field: "lpn",
        headerName: "LPN",
        flex: 1,
        minWidth: 120,
        valueGetter: (params) => {
          const vehicles = params.data.vehicles || [];
          return vehicles.length > 0
            ? vehicles.map((v: any) => v.licensePlateNumber).join(" | ")
            : "N/A";
        },
      },

      {
        field: "plan",
        headerName: "Plan",
        sortable: true,
        filter: true,
        flex: 1,
        minWidth: 130,
        cellRenderer: (params: any) => {
          const plan = params.value;
          const subscriptionId = params.data.subscriptionId;
          const url = `/user/plan/${subscriptionId}`;

          return (
            <a
              onClick={(e) => {
                e.preventDefault();
                nav(url, {
                  state: {
                    from: "parkersList",
                    parentParkerId: parkerId,
                  },
                });
              }}
              href={url}
              style={{
                color: "#d60087",
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              {plan}
            </a>
          );
        },
      },
      {
        field: "",
        headerName: "",
        sortable: false,
        filter: false,
        flex: 0,
        width: 60,
        cellRenderer: (params: any) => {
          const canDelete = !params.data.subscriptionGroupId;
          const btnStyle = { outline: "none" };
          return canDelete ? (
            <OverlayTrigger
              placement="top"
              delay={{ show: 200, hide: 0 }}
              overlay={<Tooltip id="delete-parker-tooltip">Delete Parker</Tooltip>}
            >
              <span className="d-inline-block">
                <CloseButton
                  onClick={() => {
                    setRemoveParker(params.data.parker);
                    setShowRemove(true);
                  }}
                  className="cursor-pointer"
                  style={btnStyle}
                />
              </span>
            </OverlayTrigger>
          ) : (
            <OverlayTrigger
              placement="top"
              delay={{ show: 200, hide: 0 }}
              overlay={
                <Tooltip id="delete-disabled-tooltip">Delete not available. Manage this parker directly in within the group plan</Tooltip>
              }
            >
              <span
                className="d-inline-block"
                style={{
                  cursor: "not-allowed",
                  opacity: 0.6,
                  outline: "none",
                }}
              >
                <CloseButton style={{ pointerEvents: "none" }} />
              </span>
            </OverlayTrigger>
          );
        },
      },
    ],
    [facilityId, parkerId, nav]
  );

  return (
    <Container fluid className="h-100">
      <Row className="h-100">
        <Col style={{ ...gridOneCol("max-content 1fr 5px"), display: "flex", flexDirection: "column", height: "90vh" }}>
          <div className="d-flex justify-content-between align-items-center mt-3 mb-2">
            <PageBar title="Parkers" />
            {activePlans.length > 0 && (
              <Button
                className="btn-main"
                onClick={() => setShowInviteParker(true)}
              >
                Add Parker
              </Button>
            )}
          </div>
          
          {/* Desktop View */}
          <div className="d-none d-md-block" style={{ flex: 1, minHeight: 200, height: "100%" }}>
            <DataGrid
              rowData={allGuestParkers.sort((a, b) =>
                a.name.localeCompare(b.name)
              )}
              columnDefs={columns}
            />
          </div>

          {/* Mobile View */}
          <div className="d-block d-md-none" style={{ flex: 1, overflowY: "auto" }}>
            <Form.Control
              type="search"
              placeholder="Search parkers by name, email or plate..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setVisibleCount(10);
              }}
              className="mb-3 sticky-top bg-white"
            />

            {filteredParkers.length > 0 && (
              <div className="text-muted small text-uppercase fw-semibold mb-3">
                {filteredParkers.length} {filteredParkers.length === 1 ? 'Parker' : 'Parkers'}
              </div>
            )}

            {shownParkers.length === 0 ? (
              <div className="text-center text-muted py-3">
                No parkers found
              </div>
            ) : (
              shownParkers.sort((a, b) => a.name.localeCompare(b.name)).map((parker, idx) => {
                const vehicles: Vehicle[] = parker.vehicles ?? [];
                const visiblePlates = vehicles.slice(0, MAX_VISIBLE_PLATES);
                const extraCount = vehicles.length - MAX_VISIBLE_PLATES;

                return (
                <Card key={idx} className="mb-3 rounded-4 border-1">
                  <Card.Header className="d-flex align-items-center gap-3 py-2">
                    <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 fw-bold text-white"
                         style={{ width: 30, height: 30, backgroundColor: avatarColor(parker.name), fontSize: '0.75rem' }}>
                      {getInitials(parker.name)}
                    </div>
                    <div className="flex-grow-1 min-width-0">
                      <div className="d-flex align-items-center gap-2">
                        <span className="fw-bold">{parker.name}</span>
                        {!parker.isManaged && (
                          <Badge pill bg="secondary" className="fw-normal" style={{ fontSize: '0.7rem' }}>
                            Group
                          </Badge>
                        )}
                      </div>
                      <div className="text-muted small text-truncate">{parker.isManaged ? parker.email : ''}</div>
                    </div>
                  </Card.Header>

                  {/* Action rows */}
                  <div className="border-top">
                    <div className="d-flex align-items-center gap-3 px-3 py-2 border-bottom"
                         style={{ cursor: 'pointer' }}
                         onClick={() => { setSelectedParker(parker); setShowParkerEditModal(true); }}>
                      <PersonFill size={16} className="text-muted" />
                      <div className="flex-grow-1">
                        <div className="text-muted small text-uppercase" style={{ fontSize: '0.65rem' }}>Parker</div>
                        <div className="fw-semibold small">Edit details</div>
                      </div>
                      <ChevronRight size={14} className="text-muted" />
                    </div>

                    <div className="d-flex align-items-center gap-3 px-3 py-2"
                         style={{ cursor: 'pointer' }}
                         onClick={() => nav(`/user/plan/${parker.subscriptionId}`, {
                           state: { from: "parkersList", parentParkerId: parkerId },
                         })}>
                      <PSquareFill size={16} style={{ color: '#d60087' }} />
                      <div className="flex-grow-1">
                        <div className="text-muted small text-uppercase" style={{ fontSize: '0.65rem' }}>Plan</div>
                        <div className="fw-semibold small" style={{ color: '#d60087' }}>{parker.plan}</div>
                      </div>
                      <ChevronRight size={14} className="text-muted" />
                    </div>
                  </div>

                  {/* Plates footer */}
                  <Card.Footer className="bg-white d-flex align-items-center flex-wrap gap-2 py-2">
                    <span className="text-muted small text-uppercase fw-semibold me-1">Plates</span>
                    {vehicles.length === 0 ? (
                      <span className="text-muted small fst-italic">None on file</span>
                    ) : (<>
                      {visiblePlates.map((v) => (
                        <Badge key={v.vehicleId} pill bg="light" className="text-dark border d-inline-flex align-items-center gap-1">
                          <TruckFront size={12} className="text-danger" />
                          {v.licensePlateNumber}
                        </Badge>
                      ))}
                      {extraCount > 0 && (
                        <Badge pill bg="light" className="text-muted border">
                          +{extraCount} more
                        </Badge>
                      )}
                    </>)}
                  </Card.Footer>

                  {/* Remove parker */}
                  {!parker.subscriptionGroupId && (
                    <div className="border-top d-flex align-items-center justify-content-center gap-2 px-3 py-2"
                         style={{ cursor: 'pointer' }}
                         onClick={(e) => {
                           e.stopPropagation();
                           setRemoveParker(parker.parker);
                           setShowRemove(true);
                         }}>
                      <Trash size={14} className="text-muted" />
                      <span className="text-muted small">Remove parker</span>
                    </div>
                  )}
                </Card>
                );
              })
            )}

            {visibleCount < filteredParkers.length && shownParkers.length > 0 && (
              <div className="text-center mb-3">
                <Button
                  className="btn-main"
                  onClick={() => setVisibleCount((v) => v + 10)}
                >
                  Load More
                </Button>
              </div>
            )}
          </div>
        </Col>
      </Row>

      <AddVehicleModal
        show={showAdd}
        setShow={setShowAdd}
        onSubmit={vehicleAdd}
      />

      <ChooseVehicleModal
        show={showChoose}
        setShow={setShowChoose}
        onSubmit={vehicleLink}
        vehicles={chooseModalVehicles}
        exclude={chooseModalExclude}
        onClose={() => setShowChoose(false)}
      />

      <InviteParkerModal
        show={showInvite}
        setShow={setShowInviteParker}
        submit={async (data: InviteParkerForm) => {
          const plan = inviteablePlans.find((x) => x.plan.planId === data.planId);
          setNewParkerTargetPlanId(plan!.subscriptionId);
          parkerInvite(data, undefined, plan);
        }}
        plans={inviteablePlans.map((x) => ({ ...x.plan, isUsageBasedBilling: x.isUsageBasedBilling }))}
        isEnhancedGroup={false}
      />
      <CancelParkerModal
        show={showRemove}
        onHide={() => setShowRemove(false)}
        onConfirm={(cancellationDate) => {
          if (removeParker !== undefined) {
            const targetRow = allGuestParkers.find((x) => x.parkerId === removeParker.parkerId);
            parkerRemove(removeParker.parkerId, targetRow?.subscriptionId, cancellationDate);
          }
          setShowRemove(false);
        }}
      />

      {selectedParker && (
        <ParkerEditModal
          show={showParkerEditModal}
          onClose={() => {
            setShowParkerEditModal(false);
            setSelectedParker(undefined);
          }}
          parker={selectedParker}
          setParker={setSelectedParker}
          subStatus={selectedParker.sub.status}
          onAddNew={() => setShowAdd(true)}
          onAddExisting={() => openChooseModalForParkersList()}
          onRemoveVehicle={vehicleRemove}
          subId={selectedParker.subscriptionId}
          parkers={allGuestParkers}
          setParkers={setAllGuestParkers}
          initialWaitlistRequestPayload={{
            parkerId: selectedParker.parkerId,
            facilityId: selectedParker.facilityId,
            planEntryId: selectedParker.sub.planEntryId,
            spacesRequested: selectedParker.quantity,
            isAddingToExistingParker: true,
            subscriptionId: selectedParker.subscriptionId
         }}
          isWaitlistEnabled={plans.find(x => x.plan.planId === selectedParker.planId)?.facility.isWaitlistEnabled!}
          currentSpaces={selectedParker.quantity}
          hasWaitlistEntries={plans.find(x => x.plan.planId === selectedParker.planId)?.facility.hasWaitlistEntries!}
        />
      )}

    <NoMoreSpacesModal
      show={showNoSpacesModal}
      setShow={setShowNoSpacesModal}
      planId={newParkerTargetPlanId!}
    />

    </Container>
  );
}
